import React, { useState } from 'react';
import { Clock, ArrowRight, Calendar, X } from 'lucide-react';
const ARTICLES = [{
  title: 'Understanding & Managing Daily Anxiety',
  excerpt: 'Practical grounding techniques and cognitive strategies to help you navigate moments of high anxiety and regain a sense of calm.',
  category: 'Mental Health',
  readTime: '5 min',
  date: 'March 5, 2026',
  image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Improving Communication in Relationships',
  excerpt: 'Learn the "I" statement framework and active listening skills to foster deeper connection with your partner.',
  category: 'Relationships',
  readTime: '7 min',
  date: 'February 18, 2026',
  image: 'https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Coping with Chronic Stress & Burnout',
  excerpt: 'Identifying the early warning signs of burnout and establishing healthy boundaries to protect your wellbeing.',
  category: 'Wellness',
  readTime: '6 min',
  date: 'January 24, 2026',
  image: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Building Emotional Resilience',
  excerpt: "How to develop psychological flexibility and bounce back from life's setbacks with greater strength and clarity.",
  category: 'Growth',
  readTime: '8 min',
  date: 'December 12, 2025',
  image: 'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'Navigating Grief & Loss',
  excerpt: 'Understanding the stages of grief and finding healthy ways to process your emotions during difficult times.',
  category: 'Mental Health',
  readTime: '6 min',
  date: 'November 8, 2025',
  image: 'https://images.unsplash.com/photo-1474418397713-7ede21d49118?q=80&w=800&auto=format&fit=crop'
}, {
  title: 'The Power of EMDR Therapy',
  excerpt: 'How Eye Movement Desensitization and Reprocessing can help heal trauma and reduce distressing symptoms.',
  category: 'Therapy',
  readTime: '5 min',
  date: 'October 22, 2025',
  image: 'https://images.unsplash.com/photo-1559757175-5700dde675bc?q=80&w=800&auto=format&fit=crop'
}];
const CATEGORIES = ['All', 'Mental Health', 'Relationships', 'Wellness', 'Growth', 'Therapy'];
export function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const filteredArticles = selectedCategory === 'All' ? ARTICLES : ARTICLES.filter(article => article.category === selectedCategory);
  const featuredArticle = filteredArticles[0];
  const remainingArticles = filteredArticles.slice(1);
  return <>
      {/* Hero */}
      <section className="bg-olive py-16 md:py-20" data-id="element-661">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-662">
          <div className="text-center max-w-3xl mx-auto" data-id="element-663">
            <span className="text-gold font-bold uppercase tracking-widest text-xs mb-4 block" data-id="element-664">
              BLOG & RESOURCES
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-665">
              Mental Health <span className="italic text-cream" data-id="element-666">Resources</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg font-medium" data-id="element-667">
              Insights, tools, and evidence-based articles to support your
              wellness journey, written by our clinical team to help you
              navigate life's challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-16 lg:py-20 bg-cream min-h-[60vh]" data-id="element-668">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-669">
          {/* Categories Filter & System Status */}
          <div className="mb-12" data-id="element-670">
            <div className="flex flex-wrap justify-center gap-3 mb-6" data-id="element-671">
              {CATEGORIES.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-5 py-2 rounded-full font-bold text-sm transition-all ${selectedCategory === category ? 'bg-olive text-white shadow-md' : 'bg-white text-charcoal border border-charcoal/10 hover:border-olive/50 hover:bg-olive/5'}`} aria-pressed={selectedCategory === category} data-id="element-672">
                  {category}
                </button>)}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between border-b border-charcoal/10 pb-4" data-id="element-673">
              <p className="text-charcoal/70 font-medium text-sm mb-4 sm:mb-0" data-id="element-674">
                Showing {filteredArticles.length}{' '}
                {filteredArticles.length === 1 ? 'article' : 'articles'}
                {selectedCategory !== 'All' && <span data-id="element-675">
                    {' '}
                    in{' '}
                    <strong className="text-charcoal" data-id="element-676">
                      {selectedCategory}
                    </strong>
                  </span>}
              </p>

              {selectedCategory !== 'All' && <button onClick={() => setSelectedCategory('All')} className="flex items-center text-sm font-bold text-olive hover:text-charcoal transition-colors" aria-label="Clear filter" data-id="element-677">
                  <X size={16} className="mr-1" data-id="element-678" />
                  Clear filter
                </button>}
            </div>
          </div>

          {filteredArticles.length > 0 ? <>
              {/* Featured Article (First Item) */}
              {featuredArticle && <article className="bg-white rounded-3xl overflow-hidden border border-charcoal/5 shadow-soft group mb-12 flex flex-col lg:flex-row" data-id="element-679">
                  <div className="relative lg:w-1/2 h-64 lg:h-auto overflow-hidden" data-id="element-680">
                    <img src={featuredArticle.image} alt={featuredArticle.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-681" />
                    <div className="absolute top-4 left-4" data-id="element-682">
                      <span className="text-xs font-bold tracking-wider uppercase text-white bg-olive/90 backdrop-blur-sm px-4 py-1.5 rounded-full" data-id="element-683">
                        {featuredArticle.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 md:p-12 lg:w-1/2 flex flex-col justify-center" data-id="element-684">
                    <div className="flex items-center gap-4 text-charcoal/50 text-sm font-medium mb-4" data-id="element-685">
                      <time dateTime={new Date(featuredArticle.date).toISOString()} className="flex items-center" data-id="element-686">
                        <Calendar size={14} className="mr-1.5" data-id="element-687" />
                        {featuredArticle.date}
                      </time>
                      <span className="flex items-center" data-id="element-688">
                        <Clock size={14} className="mr-1.5" data-id="element-689" />
                        {featuredArticle.readTime} read
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-charcoal mb-4 group-hover:text-olive transition-colors leading-tight" data-id="element-690">
                      {featuredArticle.title}
                    </h2>
                    <p className="text-charcoal/70 text-lg font-medium mb-8 leading-relaxed" data-id="element-691">
                      {featuredArticle.excerpt}
                    </p>
                    <div className="mt-auto" data-id="element-692">
                      <button className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-md" data-id="element-693">
                        Read Full Article
                        <ArrowRight size={16} className="ml-2" data-id="element-694" />
                      </button>
                    </div>
                  </div>
                </article>}

              {/* Remaining Articles Grid */}
              {remainingArticles.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-695">
                  {remainingArticles.map(article => <article key={article.title} className="bg-white rounded-2xl overflow-hidden border border-charcoal/5 shadow-soft group h-full flex flex-col" data-id="element-696">
                      <div className="relative h-52 overflow-hidden" data-id="element-697">
                        <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-698" />
                        <div className="absolute top-3 left-3" data-id="element-699">
                          <span className="text-xs font-bold tracking-wider uppercase text-white bg-olive/90 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-700">
                            {article.category}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 flex flex-col flex-grow" data-id="element-701">
                        <div className="flex items-center justify-between text-charcoal/40 text-xs font-medium mb-3" data-id="element-702">
                          <time dateTime={new Date(article.date).toISOString()} data-id="element-703">
                            {article.date}
                          </time>
                          <span className="flex items-center" data-id="element-704">
                            <Clock size={12} className="mr-1" data-id="element-705" />
                            {article.readTime}
                          </span>
                        </div>
                        <h2 className="font-heading text-xl font-bold text-charcoal mb-3 group-hover:text-olive transition-colors leading-snug" data-id="element-706">
                          {article.title}
                        </h2>
                        <p className="text-charcoal/60 text-base font-medium mb-6 flex-grow" data-id="element-707">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center text-olive font-bold text-sm uppercase tracking-wide group-hover:text-gold transition-colors cursor-pointer mt-auto" data-id="element-708">
                          Read Article
                          <ArrowRight size={14} className="ml-1.5 group-hover:translate-x-1 transition-transform" data-id="element-709" />
                        </div>
                      </div>
                    </article>)}
                </div>}
            </> /* Error Prevention & Recovery: Better Empty State */ : <div className="text-center py-20 bg-white rounded-3xl border border-charcoal/5 shadow-sm max-w-2xl mx-auto" data-id="element-710">
              <div className="w-16 h-16 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-711">
                <Calendar size={28} className="text-olive" data-id="element-712" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-charcoal mb-3" data-id="element-713">
                No articles found
              </h2>
              <p className="text-charcoal/60 text-lg font-medium mb-8 px-6" data-id="element-714">
                We don't have any articles in the{' '}
                <strong className="text-charcoal" data-id="element-715">{selectedCategory}</strong>{' '}
                category yet. Check back soon or explore our other resources.
              </p>
              <button onClick={() => setSelectedCategory('All')} className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-md" data-id="element-716">
                View All Articles
              </button>
            </div>}
        </div>
      </section>
    </>;
}